import { getActiveUserManager } from "@/store/userManagers";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse
} from "axios";

const instance = axios.create();

instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<any>) => {
    if (!config?.headers?.Authorization) {
      const user = await (await getActiveUserManager())?.getUser();
      if (user && user.access_token && !user.expired) {
        config.headers = Object.assign(
          {
            Authorization: `Bearer ${user.access_token}`
          },
          config.headers
        );
      }
    }

    config.validateStatus = function (status: number) {
      // For now mimic what we did before, we may change this or make it an option in the future
      const resolveOnError =
        (<AxiosRequestConfigExtra>config).resolveOnError ?? true;
      return resolveOnError ? true : status >= 200 && status < 300;
    };
    return config;
  },
  (error: AxiosResponse<any, any>) => {
    // For now mimic all errors what we did before, we may change this or make it an option in the future.
    // Default behavior returns a Promise.reject(error);
    const resolveOnError =
      (<AxiosRequestConfigExtra>error.config).resolveOnError ?? true;
    if (resolveOnError) {
      const statusResponse = <AxiosResponseWithStatus<any, any>>error;
      statusResponse.success = false;
      return statusResponse;
    } else {
      return Promise.reject(error);
    }
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse<any, any>) => {
    const statusResponse = <AxiosResponseWithStatus<any, any>>response;
    // On api auth failure force a logout
    if (response.status === 401 || response.status === 403) {
      dispatchEvent(new Event("logout"));
    }
    statusResponse.success = response.status >= 200 && response.status < 300;
    return statusResponse;
  },
  (error: AxiosResponse<any, any> | AxiosError) => {
    const config = <AxiosRequestConfigExtra>error.config;
    const status = (error as AxiosError)?.response?.status;
    const retries = config?.retries ?? 0;
    if (status !== undefined && status >= 500 && retries > 0) {
      return new Promise(resolve => {
        setTimeout(() => resolve(null), 500);
      }).then(() =>
        instance(<AxiosRequestConfigExtra>{
          ...config,
          retries: retries - 1
        })
      );
    }

    const resolveOnError = config.resolveOnError ?? true;
    if (resolveOnError) {
      const statusResponse = <AxiosResponseWithStatus<any, any>>error;
      statusResponse.success = false;
      return statusResponse;
    }

    if (status === 401 || status === 403) {
      dispatchEvent(new Event("logout"));
    }
    return Promise.reject(error);
  }
);

export default instance;

export interface AxiosRequestConfigExtra<D = any>
  extends AxiosRequestConfig<D> {
  resolveOnError: boolean;
  retries?: number;
}

export interface AxiosResponseWithStatus<T = any, D = any>
  extends AxiosResponse<T, D> {
  success: boolean;
}

export function axiosErrorHandler(
  error: any | AxiosError,
  callback: (arg0: AxiosError<any, any>) => any
) {
  if (axios.isAxiosError(error)) {
    return callback(error);
  } else {
    throw error;
  }
}
